import { useState, useEffect, useMemo, useCallback } from 'react';
import { api, collageApi } from '../../api/client';
import type { EventsResponse, CarouselEvent, Collage, EventAudio } from '../../types';
import { COLLAGE_LAYOUTS, resolveLayout, suggestLayoutForCount, DEFAULT_LAYOUT_ID } from '../../utils/collageLayouts';
import { COLLAGE_THEMES, resolveTheme, suggestThemeForMonths, DEFAULT_THEME_ID } from '../../utils/collageThemes';
import { renderCollageCanvas } from '../../utils/renderCollageCanvas';

export type EditorPhase = 'select' | 'arrange' | 'preview';
export type CollageType = 'grid' | 'storyboard';

export interface EditorPhoto {
  id: string; // EventPhoto id
  eventId: string;
  eventTitle: string;
  filePath: string;
  takenAt: string | null;
  /** Whether this photo's *event* has any voice recording — see the plan's
   * "Include recording as text" note on why this is event-level, not photo-level. */
  hasAudio: boolean;
  audioClipIds: string[];
}

export interface TextLayerState {
  id: string;
  text: string;
  xPct: number;
  yPct: number;
  size: 's' | 'm' | 'l';
  color: string;
}

export interface UseCollageEditorOptions {
  collageId?: string;
}

/**
 * Owns all Collage Editor state (mirrors useFacesReviewScan's "extract heavy
 * state logic into a hook, keep the page a thin renderer" convention). Photo
 * arrangement is tap-to-place (select a photo, then tap a slot) — no
 * drag-and-drop library, deliberately, per the project plan. Text layers are
 * the one exception: they're freely draggable since it's a single object at
 * a time, not a multi-item reorder.
 */
export function useCollageEditor(options: UseCollageEditorOptions = {}) {
  const { collageId } = options;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [phase, setPhase] = useState<EditorPhase>('select');
  const [photos, setPhotos] = useState<EditorPhoto[]>([]);
  const [eventTitlesById, setEventTitlesById] = useState<Record<string, string>>({});
  const [audioClipsByEvent, setAudioClipsByEvent] = useState<Record<string, EventAudio[]>>({});

  const [selected, setSelected] = useState<string[]>([]);
  const [type, setType] = useState<CollageType>('grid');
  const [layoutId, setLayoutId] = useState<string>(DEFAULT_LAYOUT_ID);
  const [themeId, setThemeId] = useState<string>(DEFAULT_THEME_ID);
  const [title, setTitle] = useState('');
  const [slotFill, setSlotFill] = useState<Record<number, string>>({});
  const [armedPhotoId, setArmedPhotoId] = useState<string | null>(null);
  const [storyOrder, setStoryOrder] = useState<string[]>([]);
  const [captions, setCaptions] = useState<Record<string, string>>({});
  const [sourceAudioByPhoto, setSourceAudioByPhoto] = useState<Record<string, string>>({});
  // Crop focal point per placed photo — 0-100 each, matching CSS
  // object-position exactly. Missing entries mean "centered" (50/50), the
  // fixed crop this app always used before drag-to-reposition existed.
  const [focalPoints, setFocalPoints] = useState<Record<string, { x: number; y: number }>>({});
  const [textLayers, setTextLayers] = useState<TextLayerState[]>([]);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);

  const [existingCollage, setExistingCollage] = useState<Collage | null>(null);
  const [renderedPreviewUrl, setRenderedPreviewUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);

  // ---------- load photo pool + existing collage (edit mode) ----------
  useEffect(() => {
    setLoading(true);
    api.get<EventsResponse>('/events?sort=date&allPhotos=true').then(({ data }) => {
      const events: CarouselEvent[] = data.events ?? [];
      const flat: EditorPhoto[] = [];
      const titles: Record<string, string> = {};
      const audioByEvent: Record<string, EventAudio[]> = {};
      for (const ev of events) {
        titles[ev.id] = ev.title;
        audioByEvent[ev.id] = ev.audioClips;
        const audioClipIds = ev.audioClips.map(a => a.id);
        for (const photo of ev.photos) {
          flat.push({
            id: photo.id,
            eventId: ev.id,
            eventTitle: ev.title,
            filePath: photo.filePath,
            takenAt: photo.takenAt,
            hasAudio: audioClipIds.length > 0,
            audioClipIds,
          });
        }
      }
      setPhotos(flat);
      setEventTitlesById(titles);
      setAudioClipsByEvent(audioByEvent);
    }).finally(() => {
      if (!collageId) setLoading(false);
    });
  }, [collageId]);

  useEffect(() => {
    if (!collageId) return;
    collageApi.get(collageId).then(collage => {
      setExistingCollage(collage);
      setTitle(collage.title ?? '');
      setType(collage.type);
      setLayoutId(collage.layoutId ?? DEFAULT_LAYOUT_ID);
      setThemeId(collage.themeId);
      const ids = collage.items.map(i => i.eventPhotoId);
      setSelected(ids);
      if (collage.type === 'grid') {
        const fill: Record<number, string> = {};
        collage.items.forEach(i => { fill[i.position] = i.eventPhotoId; });
        setSlotFill(fill);
      } else {
        setStoryOrder([...collage.items].sort((a, b) => a.position - b.position).map(i => i.eventPhotoId));
      }
      const caps: Record<string, string> = {};
      const audioSrc: Record<string, string> = {};
      const focals: Record<string, { x: number; y: number }> = {};
      collage.items.forEach(i => {
        if (i.caption) caps[i.eventPhotoId] = i.caption;
        if (i.sourceAudioId) audioSrc[i.eventPhotoId] = i.sourceAudioId;
        if (i.focalX !== 50 || i.focalY !== 50) focals[i.eventPhotoId] = { x: i.focalX, y: i.focalY };
      });
      setCaptions(caps);
      setSourceAudioByPhoto(audioSrc);
      setFocalPoints(focals);
      setTextLayers(collage.textLayers.map(l => ({ id: l.id, text: l.text, xPct: l.xPct, yPct: l.yPct, size: l.size, color: l.color })));
      setRenderedPreviewUrl(collage.renderedImageUrl);
    }).finally(() => setLoading(false));
  }, [collageId]);

  const photoById = useMemo(() => new Map(photos.map(p => [p.id, p])), [photos]);
  const eventGroups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, EditorPhoto[]>();
    for (const p of photos) {
      if (!map.has(p.eventId)) { map.set(p.eventId, []); order.push(p.eventId); }
      map.get(p.eventId)!.push(p);
    }
    return order.map(eventId => ({ eventId, eventTitle: eventTitlesById[eventId] ?? '', photos: map.get(eventId)! }));
  }, [photos, eventTitlesById]);

  const layout = resolveLayout(layoutId);
  const theme = resolveTheme(themeId);

  // ---------- select step ----------
  const togglePhoto = useCallback((photoId: string) => {
    setSelected(prev => prev.includes(photoId) ? prev.filter(id => id !== photoId) : [...prev, photoId]);
  }, []);

  // Lighter-weight adaptation of burstGrouping.ts's time-window burst-collapse
  // idea: full sharpness/exposure scoring needs a canvas decode per photo,
  // which would mean re-fetching every already-uploaded photo's bytes just to
  // power this convenience button — not worth the cost here. Time-spacing
  // alone (prefer photos that aren't within the same few minutes of one
  // another) still avoids picking five near-duplicate burst shots.
  const autoFillFromEvents = useCallback((eventIds: string[], cap: number) => {
    const pool = photos
      .filter(p => eventIds.includes(p.eventId))
      .slice()
      .sort((a, b) => (a.takenAt ? new Date(a.takenAt).getTime() : 0) - (b.takenAt ? new Date(b.takenAt).getTime() : 0));

    const chosen: EditorPhoto[] = [];
    const MIN_GAP_MS = 5 * 60 * 1000;
    for (const p of pool) {
      if (chosen.length >= cap) break;
      const last = chosen[chosen.length - 1];
      const gapOk = !last || !p.takenAt || !last.takenAt ||
        Math.abs(new Date(p.takenAt).getTime() - new Date(last.takenAt).getTime()) >= MIN_GAP_MS;
      if (gapOk) chosen.push(p);
    }
    // Backfill with whatever's left if spacing alone didn't reach the cap.
    if (chosen.length < cap) {
      const chosenIds = new Set(chosen.map(p => p.id));
      for (const p of pool) {
        if (chosen.length >= cap) break;
        if (!chosenIds.has(p.id)) chosen.push(p);
      }
    }
    setSelected(chosen.map(p => p.id));
  }, [photos]);

  const goToArrange = useCallback(() => {
    if (storyOrder.length === 0) setStoryOrder(selected);
    setPhase('arrange');
  }, [selected, storyOrder]);

  // ---------- arrange step: grid tap-to-place ----------
  const selectLayout = useCallback((id: string) => {
    setLayoutId(id);
    setSlotFill({});
    setArmedPhotoId(null);
  }, []);

  const armPhoto = useCallback((photoId: string) => {
    setArmedPhotoId(prev => prev === photoId ? null : photoId);
  }, []);

  const placeInSlot = useCallback((slotIndex: number) => {
    setArmedPhotoId(currentArmed => {
      if (!currentArmed) return currentArmed;
      setSlotFill(prev => {
        const next: Record<number, string> = {};
        for (const [k, v] of Object.entries(prev)) {
          if (v !== currentArmed) next[Number(k)] = v;
        }
        next[slotIndex] = currentArmed;
        return next;
      });
      return null;
    });
  }, []);

  const setCaption = useCallback((photoId: string, caption: string) => {
    setCaptions(prev => ({ ...prev, [photoId]: caption }));
  }, []);

  const setPhotoTranscript = useCallback((photoId: string, text: string, audioId: string) => {
    setCaptions(prev => ({ ...prev, [photoId]: text }));
    setSourceAudioByPhoto(prev => ({ ...prev, [photoId]: audioId }));
  }, []);

  const setPhotoFocal = useCallback((photoId: string, x: number, y: number) => {
    const clamped = { x: Math.min(100, Math.max(0, x)), y: Math.min(100, Math.max(0, y)) };
    setFocalPoints(prev => ({ ...prev, [photoId]: clamped }));
  }, []);

  const getPhotoFocal = useCallback((photoId: string) => focalPoints[photoId] ?? { x: 50, y: 50 }, [focalPoints]);

  // ---------- arrange step: storyboard order ----------
  const moveStoryItem = useCallback((index: number, dir: -1 | 1) => {
    setStoryOrder(prev => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  }, []);

  // ---------- arrange step: free text layers (grid only) ----------
  const addTextLayer = useCallback(() => {
    const id = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const layer: TextLayerState = { id, text: 'Our Story', xPct: 50, yPct: 50, size: 'm', color: theme.dark ? '#ffffff' : '#1e1b3a' };
    setTextLayers(prev => [...prev, layer]);
    setSelectedTextId(id);
  }, [theme.dark]);

  const moveTextLayer = useCallback((id: string, xPct: number, yPct: number) => {
    setTextLayers(prev => prev.map(l => l.id === id ? { ...l, xPct, yPct } : l));
  }, []);

  const updateTextLayer = useCallback((id: string, patch: Partial<Pick<TextLayerState, 'text' | 'size' | 'color'>>) => {
    setTextLayers(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  }, []);

  const removeTextLayer = useCallback((id: string) => {
    setTextLayers(prev => prev.filter(l => l.id !== id));
    setSelectedTextId(prev => prev === id ? null : prev);
  }, []);

  // ---------- auto-arrange ----------
  const autoArrange = useCallback(() => {
    const suggestedLayout = suggestLayoutForCount(selected.length);
    setLayoutId(suggestedLayout.id);
    const fill: Record<number, string> = {};
    selected.slice(0, suggestedLayout.slots.length).forEach((id, idx) => { fill[idx] = id; });
    setSlotFill(fill);

    const months = selected
      .map(id => photoById.get(id)?.takenAt)
      .filter((d): d is string => Boolean(d))
      .map(d => new Date(d).getMonth());
    const suggestedTheme = suggestThemeForMonths(months);
    setThemeId(suggestedTheme.id);

    return { layoutName: suggestedLayout.name, themeName: suggestedTheme.name };
  }, [selected, photoById]);

  // ---------- preview / save / render / publish ----------
  const orderedItems = useMemo(() => {
    if (type === 'grid') {
      return Object.entries(slotFill)
        .map(([slotIndex, photoId]) => ({ position: Number(slotIndex), eventPhotoId: photoId }))
        .sort((a, b) => a.position - b.position);
    }
    return storyOrder.map((photoId, position) => ({ position, eventPhotoId: photoId }));
  }, [type, slotFill, storyOrder]);

  const buildPayload = useCallback(() => ({
    title: title || null,
    type,
    layoutId: type === 'grid' ? layoutId : null,
    themeId,
    items: orderedItems.map(item => {
      const focal = focalPoints[item.eventPhotoId];
      return {
        eventPhotoId: item.eventPhotoId,
        position: item.position,
        caption: captions[item.eventPhotoId] || null,
        sourceAudioId: sourceAudioByPhoto[item.eventPhotoId] || null,
        focalX: focal?.x ?? 50,
        focalY: focal?.y ?? 50,
      };
    }),
    textLayers: type === 'grid' ? textLayers.map(l => ({ text: l.text, xPct: l.xPct, yPct: l.yPct, size: l.size, color: l.color })) : [],
  }), [title, type, layoutId, themeId, orderedItems, captions, sourceAudioByPhoto, focalPoints, textLayers]);

  const renderPreview = useCallback(async () => {
    if (type !== 'grid') return;
    setRendering(true);
    try {
      const items = orderedItems
        .map(item => {
          const photo = photoById.get(item.eventPhotoId);
          if (!photo) return null;
          const focal = focalPoints[item.eventPhotoId];
          return {
            slotIndex: item.position, photoUrl: photo.filePath, caption: captions[item.eventPhotoId] || null,
            focalX: focal?.x ?? 50, focalY: focal?.y ?? 50,
          };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null);

      const result = await renderCollageCanvas({
        layout, theme, items,
        textLayers: textLayers.map(l => ({ text: l.text, xPct: l.xPct, yPct: l.yPct, size: l.size, color: l.color })),
        title: title || null,
      });
      setRenderedPreviewUrl(result.dataUrl);
      return result;
    } finally {
      setRendering(false);
    }
  }, [type, orderedItems, photoById, captions, focalPoints, layout, theme, textLayers, title]);

  const goToPreview = useCallback(async () => {
    setPhase('preview');
    if (type === 'grid') await renderPreview();
  }, [type, renderPreview]);

  const save = useCallback(async (): Promise<Collage> => {
    setSaving(true);
    try {
      const payload = buildPayload();
      const saved = existingCollage
        ? await collageApi.update(existingCollage.id, payload)
        : await collageApi.create(payload);
      setExistingCollage(saved);
      return saved;
    } finally {
      setSaving(false);
    }
  }, [buildPayload, existingCollage]);

  const saveAndRenderIfGrid = useCallback(async (): Promise<Collage> => {
    const saved = await save();
    if (saved.type === 'grid') {
      const result = await renderPreview();
      if (result) {
        const { renderedImageUrl } = await collageApi.uploadRender(saved.id, result.blob);
        return { ...saved, renderedImageUrl };
      }
    }
    return saved;
  }, [save, renderPreview]);

  return {
    loading, saving, rendering, phase, setPhase,
    photos, eventGroups, photoById, audioClipsByEvent,
    selected, togglePhoto, autoFillFromEvents, goToArrange,
    type, setType,
    layout, layoutId, selectLayout, layouts: COLLAGE_LAYOUTS,
    theme, themeId, setThemeId, themes: COLLAGE_THEMES,
    title, setTitle,
    slotFill, armedPhotoId, armPhoto, placeInSlot,
    storyOrder, moveStoryItem,
    captions, setCaption, setPhotoTranscript,
    getPhotoFocal, setPhotoFocal,
    textLayers, selectedTextId, setSelectedTextId, addTextLayer, moveTextLayer, updateTextLayer, removeTextLayer,
    autoArrange,
    renderedPreviewUrl, renderPreview, goToPreview,
    existingCollage, save, saveAndRenderIfGrid,
  };
}
